/**
 * Délai (en minutes) avant le début d'une classe virtuelle en deçà duquel :
 *  - une classe ne peut plus être créée / reprogrammée (elle doit être planifiée
 *    au moins ce délai à l'avance),
 *  - une classe ne peut plus être modifiée,
 *  - le bouton « Rejoindre » se débloque.
 * Source unique de vérité pour tous ces comportements.
 */
export const MEETING_LEAD_MINUTES = 10;

export const MEETING_LEAD_MS = MEETING_LEAD_MINUTES * 60 * 1000;

/**
 * A meeting is considered finished — no longer joinable or editable — once its
 * status is 'ended' OR its scheduled end time has passed. The backend only flips
 * the status to 'ended' a couple of hours after the end (grace period for rooms
 * running late), so the front must rely on the end time to gate the UI in the
 * meantime.
 *
 * @param {object|null} meeting - meeting payload (needs `status` and `ends_at`)
 * @param {number} [now] - epoch ms to compare against (pass a reactive value to keep computeds live)
 * @returns {boolean}
 */
export default function isMeetingFinished(meeting, now = Date.now()) {
  if (!meeting) return false;
  if (meeting.status === 'ended') return true;
  // A live meeting stays joinable even if it runs past its nominal end time.
  if (meeting.status === 'started') return false;
  return !!(meeting.ends_at && new Date(meeting.ends_at).getTime() < now);
}

/**
 * A meeting can no longer be modified once we are within MEETING_LEAD_MINUTES of
 * its start (or it has already started / finished). Used to lock the settings
 * form so a class in progress — or about to start — can't be edited.
 *
 * @param {object|null} meeting - meeting payload (needs `starts_at`, `status`, `ends_at`)
 * @param {number} [now] - epoch ms to compare against (pass a reactive value to keep computeds live)
 * @returns {boolean}
 */
export function isMeetingLocked(meeting, now = Date.now()) {
  if (!meeting) return false;
  if (isMeetingFinished(meeting, now)) return true;
  const start = meeting.starts_at ? new Date(meeting.starts_at).getTime() : null;
  return !!(start && now >= start - MEETING_LEAD_MS);
}

/**
 * Formate une durée prévisionnelle (en minutes) de façon homogène entre la liste
 * des classes virtuelles et la liste des évènements du calendrier :
 *  - moins de 60 min → "45 min"
 *  - pile N heures   → "1h", "2h"
 *  - N heures + reste → "1h 05min" (minutes sur 2 chiffres)
 * Retourne null pour une durée nulle ou négative.
 *
 * @param {number} minutes
 * @returns {string|null}
 */
export function formatPlannedDuration(minutes) {
  if (!minutes || minutes <= 0) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  if (!m) return `${h}h`;
  return `${h}h ${String(m).padStart(2, '0')}min`;
}
